<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-in-out-park-statistics">
    <div class="kb-page-head">
      <div class="kb-page-title">出入园统计报表</div>
      <div class="kb-page-actions">
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
        <el-button size="small" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button
          v-if="canExport"
          size="small"
          type="success"
          icon="el-icon-download"
          :loading="exportLoading"
          :disabled="exportLoading"
          @click="handleExport"
        >
          导出 Excel
        </el-button>
        <el-tooltip v-else content="当前账号暂无导出权限" placement="top">
          <span>
            <el-button size="small" type="success" icon="el-icon-download" disabled>导出 Excel</el-button>
          </span>
        </el-tooltip>
      </div>
    </div>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="时间范围" required>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
          :clearable="false"
        />
      </el-form-item>
      <el-form-item label="货主">
        <el-select
          v-model="filters.custNo"
          clearable
          filterable
          remote
          reserve-keyword
          :remote-method="loadCustomerOptions"
          :loading="customerLoading"
          placeholder="全部货主"
          style="width: 180px"
        >
          <el-option
            v-for="item in customerOptions"
            :key="item.id"
            :label="item.deptName || item.id"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="filters.businessCategory" clearable placeholder="全部" style="width: 170px">
          <el-option
            v-for="item in businessOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="计划编号">
        <el-input
          v-model.trim="filters.planNo"
          clearable
          placeholder="输入计划编号"
          style="width: 180px"
          @keyup.enter.native="handleSearch"
        />
      </el-form-item>
    </el-form>

    <div class="kb-summary-row">
      <div v-for="item in summaryItems" :key="item.key" class="kb-summary-card">
        <div class="kb-summary-label">{{ item.label }}</div>
        <div class="kb-summary-value" :class="item.valueClass">{{ item.value }}</div>
      </div>
    </div>

    <el-alert
      v-if="errorMessage"
      class="kb-error"
      :title="errorMessage"
      type="error"
      show-icon
      :closable="false"
    />

    <div class="kb-section">
      <div class="kb-section-head">
        <span>运输计划执行明细</span>
        <small>共 {{ page.total }} 条</small>
      </div>
      <el-table
        v-loading="tableLoading"
        :data="rows"
        border
        stripe
        size="small"
        max-height="520"
        empty-text="暂无符合条件的运输计划"
        style="width: 100%"
      >
        <el-table-column prop="statDate" label="日期" width="110" />
        <el-table-column prop="planNo" label="计划编号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="planName" label="计划名称" min-width="170" show-overflow-tooltip />
        <el-table-column prop="custName" label="货主" min-width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="业务类型" width="118">
          <template slot-scope="{ row }">
            <el-tag :type="businessTagType(row.businessCategory)" size="small">
              {{ row.businessCategoryName || businessLabel(row.businessCategory) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="cargoName" label="煤种" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || '--' }}</template>
        </el-table-column>
        <el-table-column prop="planTotalWeight" label="计划量(吨)" width="120" align="right" :formatter="fmtTonCol" />
        <el-table-column prop="planExecuteWeight" label="实际执行量(吨)" width="140" align="right" :formatter="fmtTonCol" />
        <el-table-column label="执行率" width="128" align="right">
          <template slot-scope="{ row }">
            <span>{{ fmtPct(row.completionRate) }}</span>
            <div class="pct-bar">
              <i :style="{ width: barW(row.completionRate) }" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="执行后库存(吨)" width="140" align="right">
          <template slot-scope="{ row }">{{ fmtTon(row.stockRemain) }}</template>
        </el-table-column>
        <el-table-column label="计划状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="planStateTagType(row)" size="small">{{ planStateText(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="计划区间" min-width="210" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ fmtDateTime(row.intervalStartTime) }} ~ {{ fmtDateTime(row.intervalEndTime) }}
          </template>
        </el-table-column>
      </el-table>
      <div class="kb-pagination">
        <el-pagination
          background
          :current-page="page.current"
          :page-size="page.size"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <div class="kb-summary-row kb-summary-row-bottom">
      <div v-for="item in summaryItems" :key="`bottom-${item.key}`" class="kb-summary-card">
        <div class="kb-summary-label">{{ item.label }}</div>
        <div class="kb-summary-value" :class="item.valueClass">{{ item.value }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { downloadXls } from '@/util/util'
import { fetchInOutParkStatisticsPage, exportInOutParkStatistics, fetchCustomerOptions } from './api'

const EXPORT_PERMISSION_CODE = 'kanban_in_out_park_statistics_export'

const DEFAULT_PAGE = {
  current: 1,
  size: 20,
  total: 0
}

const DEFAULT_SUMMARY = {
  totalPlans: 0,
  totalPlanWeight: 0,
  totalExecuteWeight: 0,
  totalCompletionRate: 0,
  totalStockRemain: null
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`)
  d.setDate(d.getDate() + days)
  return formatDate(d)
}

function defaultDateRange() {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 6)
  return [formatDate(start), formatDate(end)]
}

function unwrapList(data) {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.records)) return data.records
  return []
}

export default {
  name: 'KanbanInOutParkStatistics',
  data() {
    return {
      loading: false,
      tableLoading: false,
      exportLoading: false,
      customerLoading: false,
      errorMessage: '',
      customerOptions: [],
      filters: {
        dateRange: defaultDateRange(),
        custNo: '',
        businessCategory: '',
        planNo: ''
      },
      businessOptions: [
        { label: '铁运入园', value: 'railInbound' },
        { label: '铁运至电厂', value: 'railToPowerPlant' },
        { label: '汽车入园', value: 'truckInbound' },
        { label: '汽车出园', value: 'truckOutbound' }
      ],
      summary: { ...DEFAULT_SUMMARY },
      rows: [],
      page: { ...DEFAULT_PAGE }
    }
  },
  computed: {
    ...mapGetters(['permission']),
    canExport() {
      const permission = this.permission || {}
      return permission[EXPORT_PERMISSION_CODE] === true
    },
    summaryItems() {
      return [
        { key: 'totalPlanWeight', label: '计划量合计', value: `${this.fmtTonValue(this.summary.totalPlanWeight)} 吨` },
        { key: 'totalExecuteWeight', label: '实际执行量合计', value: `${this.fmtTonValue(this.summary.totalExecuteWeight)} 吨` },
        {
          key: 'totalCompletionRate',
          label: '执行率',
          value: this.fmtPct(this.summary.totalCompletionRate),
          valueClass: this.rateClass(this.summary.totalCompletionRate)
        },
        { key: 'totalStockRemain', label: '执行后库存合计', value: this.summary.totalStockRemain == null ? '--' : `${this.fmtTonValue(this.summary.totalStockRemain)} 吨` },
        { key: 'totalPlans', label: '计划数', value: `${this.summary.totalPlans || 0} 条` }
      ]
    }
  },
  mounted() {
    this.loadCustomerOptions('')
    this.loadData()
  },
  methods: {
    buildQueryParams(withPage) {
      const range = this.filters.dateRange || []
      const startDate = range[0]
      const endDate = range[1] ? addDays(range[1], 1) : ''
      const data = {
        startDate,
        endDate
      }
      if (this.filters.custNo) data.custNo = this.filters.custNo
      if (this.filters.planNo) data.planNo = this.filters.planNo
      if (this.filters.businessCategory) data.businessCategory = this.filters.businessCategory
      if (withPage) {
        data.current = this.page.current
        data.size = this.page.size
      }
      return data
    },
    validateQuery() {
      const range = this.filters.dateRange || []
      if (!range[0] || !range[1]) {
        this.$message.warning('请选择时间范围')
        return false
      }
      return true
    },
    async loadCustomerOptions(keyword) {
      this.customerLoading = true
      try {
        this.customerOptions = unwrapList(await fetchCustomerOptions({ deptName: keyword || '' }))
      } catch (e) {
        this.customerOptions = []
      } finally {
        this.customerLoading = false
      }
    },
    normalizeResult(data) {
      const result = data || {}
      const pageData = result.page || {}
      const records = pageData.records || result.records || []
      this.summary = { ...DEFAULT_SUMMARY, ...(result.summary || {}) }
      this.rows = Array.isArray(records) ? records : []
      this.page = {
        current: Number(pageData.current || result.current || this.page.current || 1),
        size: Number(pageData.size || result.size || this.page.size || 20),
        total: Number(pageData.total || result.total || 0)
      }
    },
    async loadData() {
      if (!this.validateQuery()) return
      this.loading = true
      this.tableLoading = true
      this.errorMessage = ''
      try {
        const data = await fetchInOutParkStatisticsPage(this.buildQueryParams(true))
        this.normalizeResult(data)
      } catch (e) {
        this.rows = []
        this.summary = { ...DEFAULT_SUMMARY }
        this.page.total = 0
        this.errorMessage = (e && e.message) || '出入园统计报表加载失败'
        this.$message.error(this.errorMessage)
      } finally {
        this.loading = false
        this.tableLoading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handleReset() {
      this.filters = {
        dateRange: defaultDateRange(),
        custNo: '',
        businessCategory: '',
        planNo: ''
      }
      this.page.current = 1
      this.page.size = 20
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleCurrentChange(current) {
      this.page.current = current
      this.loadData()
    },
    async handleExport() {
      if (this.exportLoading) return
      if (!this.canExport) return
      if (!this.validateQuery()) return
      const total = this.page.total || 0
      if (total < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      const maxRows = 10000
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`)
        return
      }
      this.exportLoading = true
      try {
        const res = await exportInOutParkStatistics(this.buildQueryParams(false))
        downloadXls(res.data, `出入园统计报表_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        console.warn(e)
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    fmtTonCol(row, col, val) {
      return this.fmtTon(val)
    },
    fmtTon(val) {
      if (val == null || val === '') return '--'
      return this.fmtTonValue(val)
    },
    fmtTonValue(val) {
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      return n.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    fmtPct(val) {
      if (val == null || val === '') return '--'
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      return `${n.toFixed(2)}%`
    },
    fmtDateTime(val) {
      if (val == null || val === '') return '--'
      const text = String(val)
      return text.length > 19 ? text.slice(0, 19) : text
    },
    barW(pct) {
      const n = Math.min(100, Math.max(0, Number(pct) || 0))
      return `${n}%`
    },
    rateClass(pct) {
      const n = Number(pct)
      if (Number.isNaN(n)) return ''
      if (n < 70) return 'is-danger'
      if (n < 90) return 'is-warn'
      return ''
    },
    businessLabel(value) {
      const hit = this.businessOptions.find((item) => item.value === value)
      return hit ? hit.label : '--'
    },
    businessTagType(value) {
      if (value === 'truckOutbound') return 'success'
      if (value === 'truckInbound') return 'primary'
      if (value === 'railToPowerPlant') return 'warning'
      return 'info'
    },
    planStateText(row) {
      if (row.planStateName || row.planStateLabel || row.trackStatusLabel) {
        return row.planStateName || row.planStateLabel || row.trackStatusLabel
      }
      const stateMap = {
        0: '未开始',
        1: '进行中',
        2: '已完成'
      }
      return stateMap[row.planState] || '--'
    },
    planStateTagType(row) {
      const text = String(this.planStateText(row))
      if (text.indexOf('完成') > -1) return 'success'
      if (text.indexOf('进行') > -1 || text.indexOf('执行') > -1) return 'primary'
      if (text.indexOf('超') > -1 || text.indexOf('异常') > -1) return 'danger'
      if (text.indexOf('待') > -1) return 'warning'
      return 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
